import { useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { fmtDateTime, roleName } from "../components/format";
import type { Role, Territory, User } from "../api/types";

// Кем вправе управлять каждая роль — то же правило проверяет сервер.
const MANAGEABLE: Record<Role, Role[]> = {
  admin: ["inspector", "chief", "scientist", "director", "admin"],
  director: ["inspector", "chief", "scientist"],
  chief: ["inspector"],
  inspector: [],
  scientist: [],
};

const NEEDS_TERRITORY: Role[] = ["inspector", "chief"];
const NO_TERRITORY: Role[] = ["director", "admin"];

const FILTERS: [string, string, Role[] | null][] = [
  ["all", "Все", null],
  ["field", "Инспекторы", ["inspector"]],
  ["lead", "Руководители", ["chief", "director", "admin"]],
  ["science", "Наука", ["scientist"]],
];

type Form = { login: string; full_name: string; role: Role; territory_id: string };

/** Раздел «Сотрудники»: список слева, карточка или форма справа. */
export function UsersPage() {
  const { me } = useAuth();
  const users = useQuery<{ items: User[] }>("/api/v1/users");
  const territories = useQuery<{ items: Territory[] }>("/api/v1/territories");

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const visible = useMemo(() => {
    const roles = FILTERS.find(([id]) => id === filter)?.[2];
    const q = search.trim().toLowerCase();
    return (users.data?.items ?? []).filter((u) =>
      (!roles || roles.includes(u.role)) &&
      (!q || u.full_name.toLowerCase().includes(q) || u.login.toLowerCase().includes(q)));
  }, [users.data, filter, search]);

  const selected = users.data?.items.find((u) => u.id === selectedId) ?? null;
  const active = users.data?.items.filter((u) => u.is_active).length ?? 0;
  const blocked = (users.data?.items.length ?? 0) - active;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Сотрудники</h2>
          {users.data && <p className="muted small">{active} активных{blocked > 0 && ` · ${blocked} заблокировано`}</p>}
        </div>
        <button onClick={() => { setCreating(true); setSelectedId(null); }}>Новый сотрудник</button>
      </div>

      <div className="filters">
        <div className="segmented" role="group" aria-label="Роль">
          {FILTERS.map(([id, title]) => (
            <button key={id} aria-pressed={filter === id} onClick={() => setFilter(id)}>{title}</button>
          ))}
        </div>
        <label className="visually-hidden" htmlFor="user-search">Поиск</label>
        <input id="user-search" placeholder="Поиск по имени или логину" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 240 }} />
      </div>

      {users.error && <div className="error" role="alert">{users.error}</div>}
      {users.loading && !users.data && <p className="notice">Загрузка…</p>}

      <div className="split">
        <div className="split-list">
          <div className="list">
            {visible.length === 0 && users.data && <div className="empty">Никого не найдено</div>}
            {visible.map((u) => (
              <button key={u.id} className={`item${u.id === selectedId && !creating ? " selected" : ""}`}
                onClick={() => { setSelectedId(u.id); setCreating(false); }}>
                <span className="avatar" aria-hidden="true">{initials(u.full_name)}</span>
                <span className="item-body">
                  <span className="item-head">
                    <span className="item-title">{u.full_name}</span>
                    {!u.is_active && <span className="badge danger">заблокирован</span>}
                  </span>
                  <span className="item-text">
                    {roleName[u.role]}{u.territory_name && ` · ${u.territory_name}`} · {u.login}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="split-detail">
          {creating && me && (
            <UserForm
              key="new"
              title="Новый сотрудник"
              initial={{ login: "", full_name: "", role: "inspector", territory_id: me.territory_id ?? "" }}
              roles={MANAGEABLE[me.role]}
              territories={territories.data?.items ?? []}
              onDone={(id) => { users.reload(); setCreating(false); setSelectedId(id); }}
              onCancel={() => setCreating(false)}
            />
          )}
          {!creating && selected && me && (
            <UserCard key={selected.id} user={selected} roles={MANAGEABLE[me.role]}
              territories={territories.data?.items ?? []} onChanged={users.reload} />
          )}
          {!creating && !selected && (
            <div className="card empty">Выберите сотрудника или заведите нового</div>
          )}
        </div>
      </div>
    </>
  );
}

function UserCard({ user, roles, territories, onChanged }: {
  user: User; roles: Role[]; territories: Territory[]; onChanged: () => void;
}) {
  const { me } = useAuth();
  const [password, setPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось выполнить действие");
    } finally {
      setBusy(false);
    }
  }

  const resetPassword = () => act(async () => {
    if (!confirm(`Сбросить пароль сотрудника ${user.full_name}? Открытые сессии панели будут закрыты.`)) return;
    const res = await api.post<{ password: string }>(`/api/v1/users/${user.id}/password`);
    setPassword(res.password);
  });

  const toggleActive = () => act(async () => {
    if (user.is_active && !confirm(
      `Заблокировать ${user.full_name}? Вход в панель и работа телефона прекратятся сразу.`)) return;
    await api.post(`/api/v1/users/${user.id}/active`, { active: !user.is_active });
    onChanged();
  });

  return (
    <section className="card" aria-label="Карточка сотрудника">
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <div className="muted small">{user.login}</div>
          <h3 style={{ fontSize: 22, margin: "2px 0 0" }}>{user.full_name}</h3>
        </div>
        {user.can_edit && (
          <div className="filters" style={{ margin: 0 }}>
            <button className="secondary" disabled={busy} onClick={() => void resetPassword()}>Сбросить пароль</button>
            <button className={user.is_active ? "danger" : ""} disabled={busy} onClick={() => void toggleActive()}>
              {user.is_active ? "Заблокировать" : "Разблокировать"}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {password && <NewPassword login={user.login} password={password} />}

      {user.can_edit ? (
        <UserForm
          title={null}
          initial={{ login: user.login, full_name: user.full_name, role: user.role, territory_id: user.territory_id ?? "" }}
          roles={roles}
          territories={territories}
          userId={user.id}
          onDone={onChanged}
        />
      ) : (
        <div className="list" style={{ background: "var(--bg)" }}>
          <div className="row"><span className="muted">Роль</span><span>{roleName[user.role]}</span></div>
          <div className="row"><span className="muted">Территория</span><span>{user.territory_name ?? "все"}</span></div>
          <div className="row muted small">Изменять этого сотрудника вы не можете</div>
        </div>
      )}

      <div className="list" style={{ background: "var(--bg)", marginTop: 16 }}>
        <div className="row"><span className="muted">Статус</span><span>{user.is_active ? "Активен" : "Заблокирован"}</span></div>
        <div className="row"><span className="muted">Последняя работа в приложении</span><span>{fmtDateTime(user.last_seen_at)}</span></div>
        <div className="row"><span className="muted">Заведён</span><span>{fmtDateTime(user.created_at)}</span></div>
      </div>

      {/* Устройства общие: инспектор опознаётся на них PIN-кодом или жетоном,
          поэтому код задаёт начальник, а не сам инспектор на телефоне. */}
      {user.can_edit && user.role === "inspector" && <PinForm user={user} onChanged={onChanged} />}

      {/* Научному сотруднику треки недоступны вовсе — право ему ни к чему. */}
      {me?.can_grant_bulk_export && user.can_edit && user.role !== "scientist" && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginTop: 16 }}>
          <input type="checkbox" checked={user.bulk_geo_export} disabled={busy} style={{ minHeight: 0 }}
            onChange={(e) => void act(async () => {
              await api.post(`/api/v1/users/${user.id}/bulk-export`, { enabled: e.target.checked });
              onChanged();
            })} />
          Массовая выгрузка геоданных — треки всех обходов за период по его территориям
        </label>
      )}
    </section>
  );
}

/** PIN-код приложения: инспектор вводит его, принимая смену на общем устройстве. */
function PinForm({ user, onChanged }: { user: User; onChanged: () => void }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/users/${user.id}/pin`, { pin });
      setPin("");
      setDone(true);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось задать PIN-код");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} style={{ marginTop: 16 }}>
      <div className="section-title">PIN-код приложения</div>
      {error && <div className="error" role="alert">{error}</div>}
      {done && <div className="muted small">PIN-код сохранён. Устройства получат его при синхронизации.</div>}
      <div className="filters" style={{ margin: "8px 0 0", alignItems: "flex-end" }}>
        <label className="field" style={{ margin: 0 }}>{user.has_pin ? "Новый PIN-код" : "PIN-код"}
          <input value={pin} inputMode="numeric" autoComplete="off" placeholder="4–8 цифр"
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} />
        </label>
        <button type="submit" disabled={busy || pin.length < 4}>Сохранить</button>
      </div>
      <p className="muted small" style={{ marginBottom: 0 }}>
        {user.has_pin
          ? "Код задан. Сообщите инспектору новый код лично: в панели он больше не показывается."
          : "Без PIN-кода инспектор принимает смену только личным жетоном."}
      </p>
    </form>
  );
}

function UserForm({ title, initial, roles, territories, userId, onDone, onCancel }: {
  title: string | null;
  initial: Form;
  roles: Role[];
  territories: Territory[];
  userId?: string;
  onDone: (id: string) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<Form>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; password: string } | null>(null);

  const needsTerritory = NEEDS_TERRITORY.includes(form.role);
  const noTerritory = NO_TERRITORY.includes(form.role);
  const changed = JSON.stringify(form) !== JSON.stringify(initial);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { ...form, territory_id: noTerritory || !form.territory_id ? null : form.territory_id };
    try {
      if (userId) {
        await api.put(`/api/v1/users/${userId}`, body);
        onDone(userId);
      } else {
        setCreated(await api.post<{ id: string; password: string }>("/api/v1/users", body));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <section className="card">
        <h3>Сотрудник заведён</h3>
        <NewPassword login={form.login} password={created.password} />
        <button onClick={() => onDone(created.id)}>Готово</button>
      </section>
    );
  }

  const fields = (
    <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {error && <div className="error" role="alert">{error}</div>}
      <label className="field">ФИО
        <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
      </label>
      {!userId && (
        <label className="field">Логин
          <input required autoComplete="off" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
        </label>
      )}
      <label className="field">Роль
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
          {roles.map((r) => <option key={r} value={r}>{roleName[r]}</option>)}
        </select>
      </label>
      {!noTerritory && (
        <label className="field">Территория{!needsTerritory && " (необязательно)"}
          <select required={needsTerritory} value={form.territory_id}
            onChange={(e) => setForm({ ...form, territory_id: e.target.value })}>
            <option value="">{needsTerritory ? "Выберите территорию" : "Все территории"}</option>
            {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      <div className="filters" style={{ margin: 0 }}>
        <button type="submit" disabled={busy || !changed}>{userId ? "Сохранить" : "Завести"}</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Отмена</button>}
      </div>
      {!userId && <p className="muted small" style={{ margin: 0 }}>Пароль сгенерирует сервер и покажет один раз.</p>}
    </form>
  );

  return title ? <section className="card"><h3>{title}</h3>{fields}</section> : fields;
}

/**
 * Копирование в буфер. navigator.clipboard есть только в защищённом
 * контексте (https или localhost), а стенд работает по http с IP —
 * поэтому нужен запасной путь через скрытое поле.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard) {
    const ok = await navigator.clipboard.writeText(text).then(() => true, () => false);
    if (ok) return true;
  }
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  field.setSelectionRange(0, text.length);
  const ok = document.execCommand("copy");
  document.body.removeChild(field);
  return ok;
}

/** Пароль показывается один раз: после закрытия его не узнать даже администратору. */
function NewPassword({ login, password }: { login: string; password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="secret" role="status">
      <div className="small muted">Передайте сотруднику — больше пароль не покажется</div>
      <div className="secret-row">
        <span>Логин <strong>{login}</strong></span>
        <span>Пароль <strong className="mono">{password}</strong></span>
        <button type="button" className="secondary" onClick={() => {
          void copyToClipboard(password).then(setCopied);
        }}>{copied ? "Скопировано" : "Скопировать"}</button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}
